import axios from 'axios';

const API_URL = `${process.env.REACT_APP_API_URL}/api/teacher-attendance`;

export interface TeacherAttendanceCheckInRequest {
  latitude?: number;
  longitude?: number;
  accuracyMetres?: number;
  qrToken?: string;
}

export interface TeacherAttendanceCheckOutRequest {
  latitude?: number;
  longitude?: number;
  accuracyMetres?: number;
}

export interface TeacherAttendanceResponse {
  id: number;
  staffId: number;
  staffName: string;
  checkInTime: string;
  checkOutTime: string | null;
  lastCheckInTime: string | null;
  lastCheckOutTime: string | null;
  checkInMethod: string;
  geoVerified: boolean;
  totalHours: number | null;
  date: string;
  status: string;
  overrideNote: string | null;
  overrideBy: number | null;
  gpsAccuracyM: number | null;
  checkInLat: number | null;
  checkInLng: number | null;
  checkOutLat: number | null;
  checkOutLng: number | null;
  isCheckedIn: boolean;
  isCheckedOut: boolean;
  employeeId?: string;
}

export interface TeacherAttendanceSummaryDto {
  staffId: number;
  staffName: string;
  month: number;
  year: number;
  totalDaysPresent: number;
  totalDaysLate: number;
  totalDaysHalfDay: number;
  totalDaysIncomplete: number;
  totalDaysAbsent: number;
  totalDaysLeave: number;
  totalHoursWorked: number;
}

export interface TeacherAttendanceOverrideRequest {
  checkInTime: string;
  checkOutTime: string;
  status: string;
  overrideNote: string;
}

export interface StaffDto {
  id: number;
  firstName: string;
  lastName: string;
  employeeId: string;
  email: string;
  phone: string;
  schoolId: number;
  department: string;
  joiningDate: string;
  status: boolean;
  enabled: boolean;
}

export interface PageResponse<T> {
  content: T[];
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
}

// REST call functions
export const checkIn = async (payload: TeacherAttendanceCheckInRequest): Promise<TeacherAttendanceResponse> => {
  const response = await axios.post<TeacherAttendanceResponse>(`${API_URL}/check-in`, payload);
  return response.data;
};

export const checkOut = async (payload: TeacherAttendanceCheckOutRequest): Promise<TeacherAttendanceResponse> => {
  const response = await axios.post<TeacherAttendanceResponse>(`${API_URL}/check-out`, payload);
  return response.data;
};

export const getTodayStatus = async (): Promise<TeacherAttendanceResponse | null> => {
  const response = await axios.get<TeacherAttendanceResponse | null>(`${API_URL}/status/today`);
  return response.data;
};

export const getMyRecords = async (month: number, year: number): Promise<TeacherAttendanceResponse[]> => {
  const response = await axios.get<TeacherAttendanceResponse[]>(`${API_URL}/my-records`, {
    params: { month, year }
  });
  return response.data;
};

export const getSchoolReport = async (
  from: string,
  to: string,
  page: number,
  size: number
): Promise<PageResponse<TeacherAttendanceResponse>> => {
  const response = await axios.get<PageResponse<TeacherAttendanceResponse>>(`${API_URL}/school-report`, {
    params: { from, to, page, size }
  });
  return response.data;
};

export const overrideRecord = async (
  id: number,
  payload: TeacherAttendanceOverrideRequest
): Promise<TeacherAttendanceResponse> => {
  const response = await axios.put<TeacherAttendanceResponse>(`${API_URL}/${id}/override`, payload);
  return response.data;
};

export const getQrToken = async (): Promise<string> => {
  const response = await axios.get<string>(`${API_URL}/qr-token`);
  return response.data;
};

export const getMonthlySummary = async (
  staffId: number,
  month: number,
  year: number
): Promise<TeacherAttendanceSummaryDto> => {
  const response = await axios.get<TeacherAttendanceSummaryDto>(`${API_URL}/summary/${staffId}`, {
    params: { month, year }
  });
  return response.data;
};

export const getAbsentToday = async (): Promise<StaffDto[]> => {
  const response = await axios.get<StaffDto[]>(`${API_URL}/absent-today`);
  return response.data;
};

export const markAbsent = async (staffId: number, date: string, status?: string): Promise<TeacherAttendanceResponse> => {
  const response = await axios.post<TeacherAttendanceResponse>(`${API_URL}/mark-absent`, null, {
    params: { staffId, date, status }
  });
  return response.data;
};
